import { useEffect, useState } from "react";

export type Route =
  | { name: "projects" }
  | { name: "project"; id: string; tab?: string }
  | { name: "viewer"; id: string; page: number; pin?: string; sel?: string; place?: string }
  | { name: "reports"; query: string }
  | { name: "report"; id: string }
  | { name: "newReport"; projectId?: string }
  | { name: "users" }
  | { name: "profile" }
  | { name: "compare"; n?: string; np?: number; o?: string; op?: number; mode?: string };

export function parseHash(hash: string): Route {
  const [path, query = ""] = hash.replace(/^#/, "").split("?");
  const params = new URLSearchParams(query);
  const parts = (path ?? "").split("/").filter(Boolean);
  if (parts[0] === "p" && parts[1]) return { name: "project", id: parts[1], tab: params.get("tab") ?? undefined };
  if (parts[0] === "users") return { name: "users" };
  if (parts[0] === "profile") return { name: "profile" };
  if (parts[0] === "compare") { const num = (k: string) => { const v = Number(params.get(k)); return v >= 1 ? Math.floor(v) : undefined; }; return { name: "compare", n: params.get("n") ?? undefined, np: num("np"), o: params.get("o") ?? undefined, op: num("op"), mode: params.get("mode") ?? undefined }; }
  if (parts[0] === "reports" && parts[1] === "new") return { name: "newReport", projectId: params.get("project") ?? undefined };
  if (parts[0] === "reports") return { name: "reports", query };
  if (parts[0] === "r" && parts[1]) return { name: "report", id: parts[1] };
  if (parts[0] === "d" && parts[1]) return { name: "viewer", id: parts[1], page: Math.max(1, Number(params.get("page") ?? 1) || 1), pin: params.get("pin") ?? undefined, sel: params.get("sel") ?? undefined, place: params.get("place") ?? undefined };
  return { name: "projects" };
}

export function href(route: Route): string {
  switch (route.name) {
    case "projects": return "#/";
    case "project": return `#/p/${route.id}${route.tab ? `?tab=${route.tab}` : ""}`;
    case "viewer": return `#/d/${route.id}?page=${route.page}${route.pin ? `&pin=${route.pin}` : ""}${route.sel ? `&sel=${route.sel}` : ""}${route.place ? `&place=${route.place}` : ""}`;
    case "reports": return `#/reports${route.query ? `?${route.query}` : ""}`;
    case "report": return `#/r/${route.id}`;
    case "newReport": return `#/reports/new${route.projectId ? `?project=${route.projectId}` : ""}`;
    case "users": return "#/users";
    case "profile": return "#/profile";
    case "compare": { const q = new URLSearchParams(); if (route.n) q.set("n", route.n); if (route.np) q.set("np", String(route.np)); if (route.o) q.set("o", route.o); if (route.op) q.set("op", String(route.op)); if (route.mode) q.set("mode", route.mode); return `#/compare?${q}`; }
  }
}

export function navigate(route: Route, replace = false): void {
  const target = href(route);
  if (replace) history.replaceState(null, "", target);
  else location.hash = target;
  if (replace) window.dispatchEvent(new HashChangeEvent("hashchange"));
}

export function useRoute(): Route {
  const [route, setRoute] = useState(() => parseHash(location.hash));
  useEffect(() => {
    const on = () => setRoute(parseHash(location.hash));
    window.addEventListener("hashchange", on);
    return () => window.removeEventListener("hashchange", on);
  }, []);
  return route;
}
